import { mat4, vec3 } from 'gl-matrix';
import { Bvh } from './Bvh';
import { raycastBvh, BvhHit } from './BvhRayQuery';

/**
 * Top-Level Acceleration Structure — a BVH over instance AABBs. Each leaf
 * references a BLAS (Bottom-Level AS, see `BlasStore`) plus a placement
 * transform. Two-level BVH is the standard layout for instanced scenes
 * (every modern ray tracer + hardware RT uses it): one BVH per unique
 * template (built once), one BVH per tile over instance AABBs (cheap to
 * rebuild on tile load).
 *
 * Per-leaf payload:
 *   - `blas`: the template's BVH (looked up from `BlasStore` at build time)
 *   - `transform`: instance → world matrix (placement)
 *   - `inverseTransform`: world → instance matrix (precomputed once; queries
 *     use it to transform rays into BLAS-local space)
 *   - `isTerrain`: true for the per-tile terrain BLAS leaf; false for placed
 *     objects. Flows through to `FloorTagged.terrainOrigin`.
 */

export interface TlasLeaf {
  blas: Bvh;
  transform: mat4;
  inverseTransform: mat4;
  isTerrain: boolean;
}

export interface Tlas {
  readonly leaves: ReadonlyArray<TlasLeaf>;
  /** TLAS BVH over instance world-AABBs, layout identical to `Bvh`. */
  readonly nodeCount: number;
  readonly nodeBounds: Float32Array;
  readonly nodeLeftFirst: Int32Array;
  readonly nodeChildCount: Uint32Array; // 0 = internal, >0 = leaf with this many instance IDs
  /** Instance ID order — leaves reference contiguous ranges via nodeLeftFirst/nodeChildCount. */
  readonly instanceIds: Uint32Array;
  /** World AABB of the root. */
  readonly rootBounds: { min: [number, number, number]; max: [number, number, number] };
}

export interface TlasHit extends BvhHit {
  /** Index into `Tlas.leaves` of the instance that was hit. */
  instanceId: number;
  /** Convenience: leaf's `isTerrain` flag. */
  isTerrain: boolean;
}

const LEAF_THRESHOLD = 4;
const BIN_COUNT = 16;

// --- Build ---

/**
 * Build a TLAS from a list of placed instances. Each leaf's world AABB is
 * computed by transforming the BLAS root AABB's 8 corners by `transform`
 * and taking the envelope — correct for arbitrary rotation/scale.
 */
export function buildTlas(leaves: TlasLeaf[]): Tlas {
  if (leaves.length === 0) {
    return {
      leaves,
      nodeCount: 0,
      nodeBounds: new Float32Array(0),
      nodeLeftFirst: new Int32Array(0),
      nodeChildCount: new Uint32Array(0),
      instanceIds: new Uint32Array(0),
      rootBounds: { min: [0, 0, 0], max: [0, 0, 0] },
    };
  }

  const n = leaves.length;
  // Per-instance world AABB + centroid.
  // Stride 7: [worldMinX, worldMinY, worldMinZ, worldMaxX, worldMaxY, worldMaxZ, ...]
  //  + separate centroid array (stride 3) for binning.
  const aabbs = new Float32Array(n * 6);
  const centroids = new Float32Array(n * 3);
  const order = new Uint32Array(n);
  const corner = vec3.create();

  for (let i = 0; i < n; i++) {
    order[i] = i;
    const leaf = leaves[i];
    const lb = leaf.blas.rootBounds;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    // Transform each of the 8 BLAS-root corners; envelope.
    for (let cx = 0; cx < 2; cx++) {
      for (let cy = 0; cy < 2; cy++) {
        for (let cz = 0; cz < 2; cz++) {
          corner[0] = cx === 0 ? lb.min[0] : lb.max[0];
          corner[1] = cy === 0 ? lb.min[1] : lb.max[1];
          corner[2] = cz === 0 ? lb.min[2] : lb.max[2];
          vec3.transformMat4(corner, corner, leaf.transform);
          if (corner[0] < minX) minX = corner[0];
          if (corner[1] < minY) minY = corner[1];
          if (corner[2] < minZ) minZ = corner[2];
          if (corner[0] > maxX) maxX = corner[0];
          if (corner[1] > maxY) maxY = corner[1];
          if (corner[2] > maxZ) maxZ = corner[2];
        }
      }
    }
    const ai = i * 6;
    aabbs[ai] = minX; aabbs[ai + 1] = minY; aabbs[ai + 2] = minZ;
    aabbs[ai + 3] = maxX; aabbs[ai + 4] = maxY; aabbs[ai + 5] = maxZ;
    const ci = i * 3;
    centroids[ci] = (minX + maxX) * 0.5;
    centroids[ci + 1] = (minY + maxY) * 0.5;
    centroids[ci + 2] = (minZ + maxZ) * 0.5;
  }

  const maxNodes = Math.max(1, 2 * n - 1);
  const nodeBounds = new Float32Array(maxNodes * 6);
  const nodeLeftFirst = new Int32Array(maxNodes);
  const nodeChildCount = new Uint32Array(maxNodes);
  const nodes = { nodeBounds, nodeLeftFirst, nodeChildCount, count: 1 };

  buildTlasNode(aabbs, centroids, order, nodes, 0, 0, n);

  return {
    leaves,
    nodeCount: nodes.count,
    nodeBounds: nodeBounds.slice(0, nodes.count * 6),
    nodeLeftFirst: nodeLeftFirst.slice(0, nodes.count),
    nodeChildCount: nodeChildCount.slice(0, nodes.count),
    instanceIds: order,
    rootBounds: {
      min: [nodeBounds[0], nodeBounds[1], nodeBounds[2]],
      max: [nodeBounds[3], nodeBounds[4], nodeBounds[5]],
    },
  };
}

function buildTlasNode(
  aabbs: Float32Array,
  centroids: Float32Array,
  order: Uint32Array,
  nodes: { nodeBounds: Float32Array; nodeLeftFirst: Int32Array; nodeChildCount: Uint32Array; count: number },
  nodeIdx: number,
  start: number,
  end: number,
): void {
  const count = end - start;

  // Compute this node's world AABB + centroid bounds across the slice.
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  let cMinX = Infinity, cMinY = Infinity, cMinZ = Infinity;
  let cMaxX = -Infinity, cMaxY = -Infinity, cMaxZ = -Infinity;
  for (let s = start; s < end; s++) {
    const id = order[s];
    const ai = id * 6;
    if (aabbs[ai] < minX) minX = aabbs[ai];
    if (aabbs[ai + 1] < minY) minY = aabbs[ai + 1];
    if (aabbs[ai + 2] < minZ) minZ = aabbs[ai + 2];
    if (aabbs[ai + 3] > maxX) maxX = aabbs[ai + 3];
    if (aabbs[ai + 4] > maxY) maxY = aabbs[ai + 4];
    if (aabbs[ai + 5] > maxZ) maxZ = aabbs[ai + 5];
    const ci = id * 3;
    if (centroids[ci] < cMinX) cMinX = centroids[ci];
    if (centroids[ci + 1] < cMinY) cMinY = centroids[ci + 1];
    if (centroids[ci + 2] < cMinZ) cMinZ = centroids[ci + 2];
    if (centroids[ci] > cMaxX) cMaxX = centroids[ci];
    if (centroids[ci + 1] > cMaxY) cMaxY = centroids[ci + 1];
    if (centroids[ci + 2] > cMaxZ) cMaxZ = centroids[ci + 2];
  }

  const b = nodeIdx * 6;
  nodes.nodeBounds[b] = minX; nodes.nodeBounds[b + 1] = minY; nodes.nodeBounds[b + 2] = minZ;
  nodes.nodeBounds[b + 3] = maxX; nodes.nodeBounds[b + 4] = maxY; nodes.nodeBounds[b + 5] = maxZ;

  if (count <= LEAF_THRESHOLD) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeChildCount[nodeIdx] = count;
    return;
  }

  const ex = cMaxX - cMinX, ey = cMaxY - cMinY, ez = cMaxZ - cMinZ;
  let axis = 0, extent = ex, cMin = cMinX;
  if (ey > extent) { axis = 1; extent = ey; cMin = cMinY; }
  if (ez > extent) { axis = 2; extent = ez; cMin = cMinZ; }
  if (extent <= 0) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeChildCount[nodeIdx] = count;
    return;
  }

  // Bin instances by centroid along chosen axis.
  const binMin = new Float32Array(BIN_COUNT * 6);
  for (let i = 0; i < BIN_COUNT; i++) {
    const o = i * 6;
    binMin[o] = Infinity; binMin[o + 1] = Infinity; binMin[o + 2] = Infinity;
    binMin[o + 3] = -Infinity; binMin[o + 4] = -Infinity; binMin[o + 5] = -Infinity;
  }
  const binCount = new Uint32Array(BIN_COUNT);
  const scale = BIN_COUNT / extent;
  for (let s = start; s < end; s++) {
    const id = order[s];
    const c = centroids[id * 3 + axis];
    let bi = Math.floor((c - cMin) * scale);
    if (bi < 0) bi = 0;
    if (bi >= BIN_COUNT) bi = BIN_COUNT - 1;
    binCount[bi]++;
    const ai = id * 6;
    const bb = bi * 6;
    if (aabbs[ai] < binMin[bb]) binMin[bb] = aabbs[ai];
    if (aabbs[ai + 1] < binMin[bb + 1]) binMin[bb + 1] = aabbs[ai + 1];
    if (aabbs[ai + 2] < binMin[bb + 2]) binMin[bb + 2] = aabbs[ai + 2];
    if (aabbs[ai + 3] > binMin[bb + 3]) binMin[bb + 3] = aabbs[ai + 3];
    if (aabbs[ai + 4] > binMin[bb + 4]) binMin[bb + 4] = aabbs[ai + 4];
    if (aabbs[ai + 5] > binMin[bb + 5]) binMin[bb + 5] = aabbs[ai + 5];
  }

  // Sweep SAH.
  const leftArea = new Float32Array(BIN_COUNT);
  const leftCount = new Uint32Array(BIN_COUNT);
  const rightArea = new Float32Array(BIN_COUNT);
  const rightCount = new Uint32Array(BIN_COUNT);
  let aMinX = Infinity, aMinY = Infinity, aMinZ = Infinity;
  let aMaxX = -Infinity, aMaxY = -Infinity, aMaxZ = -Infinity;
  let acc = 0;
  for (let i = 0; i < BIN_COUNT; i++) {
    const bb = i * 6;
    if (binCount[i] > 0) {
      if (binMin[bb] < aMinX) aMinX = binMin[bb];
      if (binMin[bb + 1] < aMinY) aMinY = binMin[bb + 1];
      if (binMin[bb + 2] < aMinZ) aMinZ = binMin[bb + 2];
      if (binMin[bb + 3] > aMaxX) aMaxX = binMin[bb + 3];
      if (binMin[bb + 4] > aMaxY) aMaxY = binMin[bb + 4];
      if (binMin[bb + 5] > aMaxZ) aMaxZ = binMin[bb + 5];
      acc += binCount[i];
    }
    leftCount[i] = acc;
    leftArea[i] = surfaceArea(aMinX, aMinY, aMinZ, aMaxX, aMaxY, aMaxZ);
  }
  aMinX = Infinity; aMinY = Infinity; aMinZ = Infinity;
  aMaxX = -Infinity; aMaxY = -Infinity; aMaxZ = -Infinity;
  acc = 0;
  for (let i = BIN_COUNT - 1; i >= 0; i--) {
    const bb = i * 6;
    if (binCount[i] > 0) {
      if (binMin[bb] < aMinX) aMinX = binMin[bb];
      if (binMin[bb + 1] < aMinY) aMinY = binMin[bb + 1];
      if (binMin[bb + 2] < aMinZ) aMinZ = binMin[bb + 2];
      if (binMin[bb + 3] > aMaxX) aMaxX = binMin[bb + 3];
      if (binMin[bb + 4] > aMaxY) aMaxY = binMin[bb + 4];
      if (binMin[bb + 5] > aMaxZ) aMaxZ = binMin[bb + 5];
      acc += binCount[i];
    }
    rightCount[i] = acc;
    rightArea[i] = surfaceArea(aMinX, aMinY, aMinZ, aMaxX, aMaxY, aMaxZ);
  }

  const parentArea = surfaceArea(minX, minY, minZ, maxX, maxY, maxZ);
  let bestCost = count;
  let bestSplit = -1;
  for (let i = 0; i < BIN_COUNT - 1; i++) {
    const lc = leftCount[i];
    const rc = rightCount[i + 1];
    if (lc === 0 || rc === 0) continue;
    const cost = 1 + (leftArea[i] * lc + rightArea[i + 1] * rc) / parentArea;
    if (cost < bestCost) {
      bestCost = cost;
      bestSplit = i;
    }
  }

  if (bestSplit < 0) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeChildCount[nodeIdx] = count;
    return;
  }

  const splitC = cMin + (bestSplit + 1) / scale;
  let left = start;
  let right = end - 1;
  while (left <= right) {
    const id = order[left];
    if (centroids[id * 3 + axis] < splitC) {
      left++;
    } else {
      const tmp = order[left];
      order[left] = order[right];
      order[right] = tmp;
      right--;
    }
  }
  const mid = left;
  if (mid === start || mid === end) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeChildCount[nodeIdx] = count;
    return;
  }

  const leftChildIdx = nodes.count;
  nodes.count += 2;
  nodes.nodeLeftFirst[nodeIdx] = leftChildIdx;
  nodes.nodeChildCount[nodeIdx] = 0;
  buildTlasNode(aabbs, centroids, order, nodes, leftChildIdx, start, mid);
  buildTlasNode(aabbs, centroids, order, nodes, leftChildIdx + 1, mid, end);
}

function surfaceArea(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): number {
  if (minX > maxX || minY > maxY || minZ > maxZ) return 0;
  const dx = maxX - minX;
  const dy = maxY - minY;
  const dz = maxZ - minZ;
  return 2 * (dx * dy + dy * dz + dz * dx);
}

// --- Queries ---

const tlasStack = new Int32Array(128);
const localOrigin = vec3.create();
const localDir = vec3.create();
const worldNormal = vec3.create();
// Additional scratch used by the segment-test path so it doesn't allocate
// per-leaf. Reentrancy isn't a concern — JS is single-threaded.
const segLocalP1 = vec3.create();
const segLocalDir = vec3.create();

/**
 * Nearest-hit raycast through the TLAS. Returns the closest BLAS hit across
 * all candidate instances, or null on miss. Hit `t` is in WORLD parametric
 * units (same scale as `origin + t * dir`). Hit normal is transformed back
 * to world space.
 */
export function raycastTlas(
  tlas: Tlas,
  origin: vec3,
  dir: vec3,
  maxT: number,
): TlasHit | null {
  if (tlas.nodeCount === 0) return null;

  const invDx = 1 / dir[0];
  const invDy = 1 / dir[1];
  const invDz = 1 / dir[2];
  const ox = origin[0], oy = origin[1], oz = origin[2];

  let stackTop = 0;
  tlasStack[stackTop++] = 0;

  let bestT = maxT;
  let bestInstance = -1;
  let bestNormalX = 0, bestNormalY = 0, bestNormalZ = 0;
  let bestTriIndex = -1;

  const bounds = tlas.nodeBounds;
  const leftFirst = tlas.nodeLeftFirst;
  const childCount = tlas.nodeChildCount;
  const instanceIds = tlas.instanceIds;

  while (stackTop > 0) {
    const nodeIdx = tlasStack[--stackTop];
    const b = nodeIdx * 6;
    const tEnter = aabbRayEnter(
      bounds[b], bounds[b + 1], bounds[b + 2],
      bounds[b + 3], bounds[b + 4], bounds[b + 5],
      ox, oy, oz, invDx, invDy, invDz,
      bestT,
    );
    if (tEnter < 0) continue;

    const cc = childCount[nodeIdx];
    if (cc > 0) {
      // Leaf — test each instance.
      const first = leftFirst[nodeIdx];
      for (let i = 0; i < cc; i++) {
        const instanceId = instanceIds[first + i];
        const leaf = tlas.leaves[instanceId];
        // Transform ray into BLAS-local space.
        vec3.transformMat4(localOrigin, origin, leaf.inverseTransform);
        transformDir(localDir, dir, leaf.inverseTransform);
        const hit = raycastBvh(leaf.blas, localOrigin, localDir, bestT);
        if (hit !== null && hit.t < bestT) {
          bestT = hit.t;
          bestInstance = instanceId;
          bestTriIndex = hit.triIndex;
          // Transform local normal back to world using the rotation+scale
          // portion of `transform`. Normalize after.
          transformDir(worldNormal, hit.normal, leaf.transform);
          const nlen = Math.sqrt(
            worldNormal[0] * worldNormal[0] +
            worldNormal[1] * worldNormal[1] +
            worldNormal[2] * worldNormal[2],
          );
          if (nlen > 0) {
            const inv = 1 / nlen;
            bestNormalX = worldNormal[0] * inv;
            bestNormalY = worldNormal[1] * inv;
            bestNormalZ = worldNormal[2] * inv;
          }
        }
      }
    } else {
      // Internal — push both children front-to-back.
      const left = leftFirst[nodeIdx];
      const right = left + 1;
      const lb = left * 6, rb = right * 6;
      const tL = aabbRayEnter(
        bounds[lb], bounds[lb + 1], bounds[lb + 2],
        bounds[lb + 3], bounds[lb + 4], bounds[lb + 5],
        ox, oy, oz, invDx, invDy, invDz,
        bestT,
      );
      const tR = aabbRayEnter(
        bounds[rb], bounds[rb + 1], bounds[rb + 2],
        bounds[rb + 3], bounds[rb + 4], bounds[rb + 5],
        ox, oy, oz, invDx, invDy, invDz,
        bestT,
      );
      if (tL < 0 && tR < 0) continue;
      if (tL < 0) {
        tlasStack[stackTop++] = right;
      } else if (tR < 0) {
        tlasStack[stackTop++] = left;
      } else if (tL <= tR) {
        tlasStack[stackTop++] = right;
        tlasStack[stackTop++] = left;
      } else {
        tlasStack[stackTop++] = left;
        tlasStack[stackTop++] = right;
      }
    }
  }

  if (bestInstance < 0) return null;
  return {
    t: bestT,
    triIndex: bestTriIndex,
    normal: vec3.fromValues(bestNormalX, bestNormalY, bestNormalZ),
    instanceId: bestInstance,
    isTerrain: tlas.leaves[bestInstance].isTerrain,
  };
}

/** Any-hit segment test through the TLAS. p0 → p1, returns true on first hit. */
export function segmentHitTlas(tlas: Tlas, p0: vec3, p1: vec3): boolean {
  if (tlas.nodeCount === 0) return false;

  const ox = p0[0], oy = p0[1], oz = p0[2];
  const dx = p1[0] - ox, dy = p1[1] - oy, dz = p1[2] - oz;
  const invDx = 1 / dx, invDy = 1 / dy, invDz = 1 / dz;

  let stackTop = 0;
  tlasStack[stackTop++] = 0;

  const bounds = tlas.nodeBounds;
  const leftFirst = tlas.nodeLeftFirst;
  const childCount = tlas.nodeChildCount;
  const instanceIds = tlas.instanceIds;

  while (stackTop > 0) {
    const nodeIdx = tlasStack[--stackTop];
    const b = nodeIdx * 6;
    const tEnter = aabbRayEnter(
      bounds[b], bounds[b + 1], bounds[b + 2],
      bounds[b + 3], bounds[b + 4], bounds[b + 5],
      ox, oy, oz, invDx, invDy, invDz,
      1.0,
    );
    if (tEnter < 0) continue;

    const cc = childCount[nodeIdx];
    if (cc > 0) {
      const first = leftFirst[nodeIdx];
      for (let i = 0; i < cc; i++) {
        const instanceId = instanceIds[first + i];
        const leaf = tlas.leaves[instanceId];
        vec3.transformMat4(localOrigin, p0, leaf.inverseTransform);
        vec3.transformMat4(segLocalP1, p1, leaf.inverseTransform);
        // Inline segment check on the local BLAS — reuse raycastBvh with t∈[0,|dir|].
        segLocalDir[0] = segLocalP1[0] - localOrigin[0];
        segLocalDir[1] = segLocalP1[1] - localOrigin[1];
        segLocalDir[2] = segLocalP1[2] - localOrigin[2];
        const hit = raycastBvh(leaf.blas, localOrigin, segLocalDir, 1.0);
        if (hit !== null) return true;
      }
    } else {
      const left = leftFirst[nodeIdx];
      tlasStack[stackTop++] = left + 1;
      tlasStack[stackTop++] = left;
    }
  }
  return false;
}

/** Apply only the rotation+scale portion of a mat4 to a direction vector (no translation). */
function transformDir(out: vec3, v: vec3, m: mat4): void {
  const x = v[0], y = v[1], z = v[2];
  out[0] = m[0] * x + m[4] * y + m[8] * z;
  out[1] = m[1] * x + m[5] * y + m[9] * z;
  out[2] = m[2] * x + m[6] * y + m[10] * z;
}

function aabbRayEnter(
  bMinX: number, bMinY: number, bMinZ: number,
  bMaxX: number, bMaxY: number, bMaxZ: number,
  ox: number, oy: number, oz: number,
  invDx: number, invDy: number, invDz: number,
  maxT: number,
): number {
  let tMin: number, tMax: number;
  if (invDx >= 0) { tMin = (bMinX - ox) * invDx; tMax = (bMaxX - ox) * invDx; }
  else { tMin = (bMaxX - ox) * invDx; tMax = (bMinX - ox) * invDx; }
  let tyMin: number, tyMax: number;
  if (invDy >= 0) { tyMin = (bMinY - oy) * invDy; tyMax = (bMaxY - oy) * invDy; }
  else { tyMin = (bMaxY - oy) * invDy; tyMax = (bMinY - oy) * invDy; }
  if (tMin > tyMax || tyMin > tMax) return -1;
  if (tyMin > tMin) tMin = tyMin;
  if (tyMax < tMax) tMax = tyMax;
  let tzMin: number, tzMax: number;
  if (invDz >= 0) { tzMin = (bMinZ - oz) * invDz; tzMax = (bMaxZ - oz) * invDz; }
  else { tzMin = (bMaxZ - oz) * invDz; tzMax = (bMinZ - oz) * invDz; }
  if (tMin > tzMax || tzMin > tMax) return -1;
  if (tzMin > tMin) tMin = tzMin;
  if (tzMax < tMax) tMax = tzMax;
  if (tMax < 0 || tMin > maxT) return -1;
  return Math.max(0, tMin);
}
